import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
  InputGroup,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { SelectField } from '@/components/SelectField';
import { CategoryVideo, Video } from '@/model';
import { usePutVideoMutation } from '@/services/queries/video';
import { ImageUpload } from '@/components/ImageUpload';
import { PutMediaVideoRequest } from '@/services/request/video';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface VideoEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Video;
  categoryVideos: CategoryVideo[];
  onClose: () => void;
  onDone?: () => void;
}

export const VideoEditModal = ({ isOpen, onClose, item, categoryVideos, onDone }: VideoEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putItemMutation = usePutVideoMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid,
      title: item.title,
      category_video_uuid: item.category_video_uuid,
      video_url: item.video_url,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PutMediaVideoRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        title: data.title,
        category_video_uuid: data.category_video_uuid,
        video_url: data.video_url,
        thumbnail: data.file,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_videos_channel.components.video_edit_modal.video_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_videos_channel.components.video_edit_modal.video_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_videos_channel.components.video_edit_modal.video_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_videos_channel.components.video_edit_modal.video_edit_team_label')}
              placeholder={t('pages.application_videos_channel.components.video_edit_modal.video_edit_team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_videos_channel.components.video_edit_modal.video_edit_title_label')}
              {...register('title')}
              helperText={t('pages.application_videos_channel.components.video_edit_modal.video_edit_title_helper_text')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputGroup display='flex' flexDirection='column'>
              <Text fontWeight='medium'>
                {t('pages.application_videos_channel.components.video_edit_modal.video_edit_category_label')}
              </Text>
              <SelectField
                {...register('category_video_uuid')}
                rootProps={{ isInvalid: !!errors?.category_video_uuid, errorMessage: errors?.category_video_uuid?.message }}
              >
                <option value='' disabled>
                  {t('pages.application_videos_channel.components.video_edit_modal.video_edit_category_default_option')}
                </option>
                {categoryVideos?.map(item => (
                  <option key={item.uuid} value={item.uuid}>
                    {item.title}
                  </option>
                ))}
              </SelectField>
            </InputGroup>

            <InputField
              withoutFloating
              label={t('pages.application_videos_channel.components.video_edit_modal.video_edit_video_url_label')}
              {...register('video_url')}
              maxLength={255}
              rootProps={{ isInvalid: !!errors?.video_url, errorMessage: errors?.video_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_videos_channel.components.video_edit_modal.video_edit_thumbnail_label')}
                imageURL={item.image_thumbnail ?? ''}
                onFileChange={file => {
                  setValue('file', file);
                }}
                helpText={t('pages.application_videos_channel.components.video_edit_modal.video_edit_thumbnail_helper_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_videos_channel.components.video_edit_modal.video_edit_cancel_button')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_videos_channel.components.video_edit_modal.video_edit_submit_button')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
